// The backend's current verification verdict per finding id, for a surface
// that only labels findings (the canvas, the add-to-workspace list): labels,
// never the verifier's quoted text.
import { useEffect, useMemo, useState } from "react";

const RECENT_MS = 90 * 86400 * 1000;
// One empty value, so a surface with nothing to label never re-renders for it.
const NONE = Object.freeze({});

// `reviewWindow` defaults to the last 90 days, the graph's widest read.
export function useFindingVerdicts(ids, reviewWindow = null) {
    const wanted = useMemo(() => [...new Set((ids || []).filter(Boolean).map(String))].sort(), [ids]);
    const key = wanted.join(",");
    const fromMs = reviewWindow?.fromMs ?? null;
    const toMs = reviewWindow?.toMs ?? null;
    const [verdicts, setVerdicts] = useState(NONE);
    useEffect(() => {
        if (!wanted.length) { setVerdicts(NONE); return undefined; }
        const controller = new AbortController();
        const now = Date.now();
        const window = fromMs != null && toMs != null ? { fromMs, toMs } : { fromMs: now - RECENT_MS, toMs: now };
        // Loaded on first use: the canvas mounts without the analytics client.
        import("./findingsTriage.js").then(({ fetchVerdicts }) => fetchVerdicts({ ids: wanted, reviewWindow: window, sensitive: false, signal: controller.signal })).then(
            (v) => { if (!controller.signal.aborted) setVerdicts(v || NONE); },
            () => { if (!controller.signal.aborted) setVerdicts(NONE); },
        );
        return () => controller.abort();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key, fromMs, toMs]);
    return verdicts;
}
